import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { IconDollarBold } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import ImgInCircle from 'components/UI/ImgInCircle/ImgInCircle'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import { DropdownCardSelector } from 'components-ref/molecules/wallet/SelectCard/DropdownCardSelector'
import { useModalContext } from 'context/modalContext'
import { AllIcons } from 'helpers/allIcons'
import { updateProfileCacheFromResponse } from 'helpers/profilecache/updateProfileBalanceFromResponse'
import { sendTipToPost } from 'services/endpoints/post'
import { sendTipToModel } from 'services/endpoints/profile'
import { sendTipToStory } from 'services/endpoints/story'
import { sendTipToStream } from 'services/endpoints/streaming'
import { useUserProfile } from 'store'
import colors from 'styles/colors'
import { type ITipPayload } from 'types/interfaces/ITypes'
import { calculatePriceWithFee } from 'utils/calculatePriceWithFee'

import './_tipModal.scss'

interface TipModalProps {
  tipType?: 'model' | 'post' | 'story' | 'stream'
  avatar?: string
  type?: string
  id?: number
  onClose?: () => any
  modelData: {
    postId?: string
    storyId?: string
    stream_id?: string
    goal_id?: string
    modelId: string
    avatarSrc: string | null
  }
}

const TipModal = ({ tipType = 'model', modelData, onClose }: TipModalProps) => {
  const [tipAmt, setTipAmt] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [hasError, setHasError] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [personalMessageVissible, setPersonalMessageVissible] = useState(false)

  const navigate = useNavigate()
  const [taxRate, setTaxRate] = useState<number | null>(0)
  const modalData = useModalContext()
  const userData = useUserProfile()
  if (!userData) return null

  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const cardsMemo: any[] = useMemo(
    () => (Array.isArray((userData as any)?.cards) ? (userData as any).cards : []),
    [userData]
  )

  const walletHasFunds = (userData?.wallet_deposit ?? 0) > 1

  const defaultCardId = useMemo(
    () => userData?.default_card ?? undefined,
    [userData?.default_card]
  )

  const [paymentMethod, setPaymentMethod] = useState<{
    method: 'card' | 'deposit'
    cardId: string | undefined
  }>({
    method: walletHasFunds ? 'deposit' : 'card',
    cardId: walletHasFunds ? undefined : defaultCardId
  })

  const tipAmountNumber = Number(tipAmt)
  const [taxRateFee, setTaxRateFee] = useState<number | null>(null)
  const [cardFee, setCardFee] = useState(0)

  const walletBalance = Number(userData?.wallet_deposit ?? 0)

  const walletInsufficient =
    paymentMethod.method === 'deposit' &&
    tipAmt.trim() !== '' &&
    Number.isFinite(tipAmountNumber) &&
    tipAmountNumber > walletBalance

  const calculatedPriceTotal = calculatePriceWithFee({
    method: paymentMethod.method,
    price: tipAmountNumber,
    cardFee
  })

  const MAX_TIP_MESSAGE_LENGTH = 25
  const TIP_MESSAGE_REGEX = /^[a-zA-Z0-9\s.!?,'"():-]*$/

  const sanitizeTipMessage = (value: string) =>
    value.slice(0, MAX_TIP_MESSAGE_LENGTH)

  const isValidTipMessage = (value: string) =>
    TIP_MESSAGE_REGEX.test(value)


  const showRestrictedModal = () => {
    modalData.addModal(
      '',
      <ConfirmModal
        body='Account is Restricted'
        confirmText='OK'
        confirmFn={() => {}}
      />,
      undefined,
      false,
      'confirmModal'
    )
  }

  const formatTipAmount = (val: number): string =>
    Number.isInteger(val) ? val.toString() : val.toFixed(2)

  useEffect(() => {
    setTaxRateFee(null)

    if (paymentMethod.method !== 'card') return
    if (!cardsMemo.length || !paymentMethod?.cardId) return

    const selectedCard = cardsMemo.find(
      (card: any) => card?.id === paymentMethod.cardId
    )

    const rawFee =
      selectedCard?.region?.fee !== undefined &&
      selectedCard?.region?.fee !== null &&
      Number(selectedCard.region.fee) > 0
        ? selectedCard.region.fee
        : selectedCard?.country?.fee

    const fee = Number(rawFee)

    setTaxRateFee(Number.isFinite(fee) ? fee : null)
  }, [cardsMemo, paymentMethod?.cardId, paymentMethod.method])

useEffect(() => {
  if (paymentMethod.method !== 'card') {
    setTaxRate(null)
    return
  }

  const amount = Number(tipAmt)
  const rate = Number(taxRateFee)

  if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(rate)) {
    setTaxRate(null)
    return
  }

  const tax = Math.round(amount * (rate / 100) * 100) / 100

  setTaxRate(tax)
}, [tipAmt, paymentMethod.method, taxRateFee])

  useEffect(() => {
    validateTipping()
  }, [])

  useEffect(() => {
    if (!userData) return

    const walletGood = (userData.wallet_deposit ?? 0) > 1
    const defaultCard = userData.default_card ?? undefined

    const nextMethod: 'card' | 'deposit' = walletGood ? 'deposit' : 'card'
    const nextCardId = walletGood ? undefined : defaultCard

    selectCard({ method: nextMethod, cardId: nextCardId })
  }, [userData])

  useEffect(() => {
    validateTipping(tipAmt)
  }, [paymentMethod, tipAmt])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
      validateTipping(value)
      setTipAmt(value)
    }
  }

  const sendTipToStreamMutation = useMutation(
    async (payload: ITipPayload) => await sendTipToStream(payload),
    {
      onSuccess: (response) => {
        const backend = response?.data || response
        if (backend && typeof backend.status !== 'undefined' && backend.status === 406 && backend.message) {
          setHasError(true)
          setErrorMessage(backend.message)
          return
        }

        modalData.clearModal()
        onClose?.()
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.message ??
          error?.message ??
          ''

        if (message === 'Your account is restricted') {
          modalData.clearModal()
          onClose?.()
          showRestrictedModal()
          return
        }

        modalData.clearModal()
        onClose?.()
        addToast('error', t('error:anErrorOccuredPleaseTryAgain'))
      },
      onSettled: () => {
        queryClient.invalidateQueries('user-profile')
      }
    }
  )

  const sendTipToModelMutation = useMutation(
    async (payload: ITipPayload) => await sendTipToModel(payload),
    {
      onSuccess: (response) => {
        const backend = response?.data || response
        if (backend && typeof backend.status !== 'undefined' && backend.status === 406 && backend.message) {
          setHasError(true)
          setErrorMessage(backend.message)
          return
        }
        modalData.clearModal()
        onClose?.()
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.message ??
          error?.message ??
          ''

        if (message === 'Your account is restricted') {
          modalData.clearModal()
          onClose?.()
          showRestrictedModal()
          return
        }

        modalData.clearModal()
        onClose?.()
        addToast('error', t('error:anErrorOccuredPleaseTryAgain'))
      },
      onSettled: () => {
        queryClient.invalidateQueries('user-profile')
      }
    }
  )

  const sendTipToPostMutation = useMutation(
    async (payload: ITipPayload) => await sendTipToPost(payload),
    {
      onSuccess: (response) => {
        const backend = response?.data || response
        if (backend && typeof backend.status !== 'undefined' && backend.status === 406 && backend.message) {
          setHasError(true)
          setErrorMessage(backend.message)
          return
        }

        updateProfileCacheFromResponse(response, userData)

        modalData.clearModal()
        onClose?.()
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.message ??
          error?.message ??
          ''

        if (message === 'Your account is restricted') {
          modalData.clearModal()
          onClose?.()
          showRestrictedModal()
          return
        }

        modalData.clearModal()
        onClose?.()
        addToast('error', t('error:anErrorOccuredPleaseTryAgain'))
      },
    }
  )

  const sendTipToStoryMutation = useMutation(
    async (payload: ITipPayload) => await sendTipToStory(payload),
    {
      onSuccess: (response) => {
        const backend = response?.data || response
        if (backend && typeof backend.status !== 'undefined' && backend.status === 406 && backend.message) {
          setHasError(true)
          setErrorMessage(backend.message)
          return
        }
        modalData.clearModal()
        onClose?.()
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.message ??
          error?.message ??
          ''

        if (message === 'Your account is restricted') {
          modalData.clearModal()
          onClose?.()
          showRestrictedModal()
          return
        }

        modalData.clearModal()
        onClose?.()
        addToast('error', t('error:anErrorOccuredPleaseTryAgain'))
      },
      onSettled: () => {
        queryClient.invalidateQueries('user-profile')
      }
    }
  )

  const submitTip = () => {
    if (walletInsufficient) {
      modalData.clearModal()
      onClose?.()
      navigate('/settings/general/wallet')
      return
    }

    if (tipAmt.trim() !== '' && !hasError) {
      let payload: ITipPayload & { goal_id?: string } = {
        model_id: modelData.modelId,
        amount: tipAmt,
        payment_method: paymentMethod.method,
        message: message.trim() || undefined
      }

      if (paymentMethod.method === 'card') {
        payload = {
          ...payload,
        }
        payload.card_id = paymentMethod.cardId
      }

      if (tipType === 'post') {
        payload.post_id = modelData.postId
      }

      if (tipType === 'story') {
        payload.story_id = modelData.storyId
      }

      if (tipType === 'stream') {
        payload.stream_id = modelData.stream_id

        if (modelData.goal_id) {
          payload.goal_id = modelData.goal_id
        }
      }

      if (tipType === 'model') {
        sendTipToModelMutation.mutate(payload)
      } else if (tipType === 'post') {
        sendTipToPostMutation.mutate(payload)
      } else if (tipType === 'story') {
        sendTipToStoryMutation.mutate(payload)
      } else if (tipType === 'stream') {
        sendTipToStreamMutation.mutate(payload)
      }
    } else {
      addToast('error', t('pleaseInputTipAmount'))
    }
  }

const validateTipping = (result?: string) => {
  const min = Number(userData.price_limits.min_tip)
  const max = Number(userData.price_limits.max_tip)
  const amount = Number(result)

  if (userData.available_daily_limit <= min) {
    setErrorMessage(`${t('error:dailyLimitExceeded')}`)
    setHasError(true)
    return
  }

  if (
    result &&
    (!Number.isFinite(amount) || amount < min || amount > max)
  ) {
    setHasError(true)
    setErrorMessage(
      `${t('error:tippingError')} $${min} USD - $${max} USD`
    )
    return
  }

  setHasError(false)
  setErrorMessage('')
}
  const selectCard = (method: { method: 'card' | 'deposit'; cardId: string | undefined }) => {
    if (paymentMethod.method === method.method && paymentMethod.cardId === method.cardId) return
    setPaymentMethod(method)
  }

const isTipAmountLess =
  paymentMethod.method === 'deposit' &&
  (userData?.wallet_deposit ?? 0) < Number(userData.price_limits.min_tip)

  const isPlaceholder =
    !modelData.avatarSrc ||
    modelData.avatarSrc.includes('/static') ||
    modelData.avatarSrc.toLowerCase().includes('placeholder') ||
    !/^https?:\/\/.+/.test(modelData.avatarSrc)

  const safeAvatarSrc: string | undefined = isPlaceholder
    ? AllIcons.user_placeholder
    : (modelData.avatarSrc ?? undefined)

  return (
    <div className='tip-modal'>
      <div className='tip-modal-input-avatar-container'>
        <div className={`tip-modal-input-wrapper ${tipAmt !== '' ? 'tip-modal-input-active' : ''}`}>
          <ImgInCircle type='tipAvatar' hasLoader={true} customClass='subscribe-profile-modal-avatar'>
            <div className={`test-avatar ${isPlaceholder ? 'placeholder' : 'real'}`}>
              <img
                src={safeAvatarSrc}
                alt={t('profileAvatar')}
                onError={(e) => {
                  e.currentTarget.onerror = null
                  e.currentTarget.src = AllIcons.user_placeholder
                }}
              />
            </div>
          </ImgInCircle>

          <input
            type='text'
            pattern='[0-9]*'
            className={`tip-modal-input ${hasError ? 'error' : ''}`}
            value={tipAmt}
            onChange={handleChange}
          />
          <div className='input-modal-dollar'>
            <IconDollarBold />
          </div>

          {tipAmt !== '' && taxRateFee !== null && paymentMethod.method === 'card' && (
            <div className='taxRate'>
              <p className='text-gray-500'>{t('TAX')}</p>+<span>${(taxRate ?? 0).toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>

      <p className={`tip-modal-min-text ${hasError ? 'error' : ''}`}>
        {hasError ? (
          <>{errorMessage}</>
        ) : (
          <>
            {t('minimum')}{' '}
            <span>
              ${formatTipAmount(Number(userData.price_limits.min_tip))} USD
            </span>{' '}
            {t('maximum')}{' '}
            <span>
              ${formatTipAmount(Number(userData.price_limits.max_tip))} USD
            </span>
          </>
        )}
      </p>

      {Number.isFinite(tipAmountNumber) && cardFee !== 0 && paymentMethod.method === 'card' && (
        <PriceWrapper>
          <PriceDescription>
            {t('Fee')}: <PriceFee>${parseFloat((tipAmountNumber * (cardFee / 100)).toString())}</PriceFee>
          </PriceDescription>
          <PriceDescription>
            {t('total')}: <PriceFee>${parseFloat(calculatedPriceTotal.toString()).toFixed(2)}</PriceFee>
          </PriceDescription>
        </PriceWrapper>
      )}

      <DropdownCardSelector
        paymentMethod={paymentMethod}
        setPaymentMethod={selectCard}
        cardList={{ cards: cardsMemo }}
        walletCanCover={!walletInsufficient}
        walletRequired={
          walletInsufficient
            ? tipAmountNumber - walletBalance
            : undefined
        }
      />

      <div
        className='tip-modal-personal-message-button'
        onClick={() => setPersonalMessageVissible(!personalMessageVissible)}
      >
        {t('addPersonalMessage')}
      </div>

      <div
        className={`tip-modal-personal-message-text-wrapper ${
          personalMessageVissible ? 'tip-modal-personal-message-text-wrapper-open' : ''
        }`}
      >
    <textarea
      className='tip-modal-personal-message-text'
      rows={5}
      maxLength={MAX_TIP_MESSAGE_LENGTH}
      value={message}
      onChange={e => {
        const next = sanitizeTipMessage(e.target.value)

        if (isValidTipMessage(next)) {
          setMessage(next)
        }
      }}
    />
      </div>
            {paymentMethod.method === 'card' && (
      <p style={{ fontSize: '11px', paddingTop: 16, paddingLeft:5, paddingRight:5 }}>The charge on your credit card bill will appear as Innodev Solutions Inc on your credit card statement</p>
      )}
      <div className='tip-modal-buttons-conainer'>
        <Button
          text={t('cancel')}
          color='grey'
          font='mont-14-normal'
          width='13'
          height='3'
          clickFn={() => modalData.clearModal()}
        />
<Button
  color={walletInsufficient ? 'black' : 'blue'}
  text={
    walletInsufficient
      ? t('loadWallet')
      : t('send')
  }
  customClass={`tip-modal-send-button ${
    sendTipToModelMutation.isLoading ||
    sendTipToPostMutation.isLoading ||
    sendTipToStoryMutation.isLoading ||
    sendTipToStreamMutation.isLoading
      ? 'tip-modal-send-button-loading'
      : ''
  }`}
  clickFn={submitTip}
/>
      </div>

    </div>
  )
}

export default TipModal

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: end;
  align-items: end;
  margin-bottom: 1rem;
`

const PriceDescription = styled.p`
  color: ${() => colors.grey};
  font-size: 1.2rem;
  font-weight: 500;
  display: flex;
  span {
    margin-left: 0.5rem;
  }
`

const PriceFee = styled.span`
  color: ${() => colors.darkGray};
  font-weight: 600;
`